import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, LinearProgress, Stack, CircularProgress } from '@mui/material';
import { CheckCircle as CheckIcon, RadioButtonUnchecked as UncheckedIcon } from '@mui/icons-material';
import axios from 'axios';
import logger from '../utils/logger';

const POLL_INTERVAL = 20000;

const getApiBase = () => process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Read-only view of a maid's task-list progress for a job, polled so the
// client sees updates as the maid checks tasks off during the job.
const ClientJobTaskProgress = ({ jobId }) => {
  const [tasks, setTasks] = useState([]);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const [tasksRes, progressRes] = await Promise.all([
        axios.get(`${apiBase}/api/jobs/${jobId}/tasks`, { headers }),
        axios.get(`${apiBase}/api/jobs/${jobId}/tasks/progress`, { headers }),
      ]);
      setTasks(tasksRes.data || []);
      setProgress(progressRes.data || null);
    } catch (error) {
      logger.log('Error fetching task progress:', error);
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    load();
    const intervalId = setInterval(load, POLL_INTERVAL);
    return () => clearInterval(intervalId);
  }, [load]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (tasks.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
        No tasks have been added to this job yet.
      </Typography>
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Box sx={{ flex: 1 }}>
          <LinearProgress
            variant="determinate"
            value={progress?.progressPercentage || 0}
            color={progress?.progressPercentage >= 100 ? 'success' : 'primary'}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {progress?.completedTasks || 0} / {progress?.totalTasks || tasks.length} tasks
        </Typography>
      </Stack>

      <Stack spacing={0.5}>
        {tasks.map((task) => (
          <Stack key={task.id} direction="row" alignItems="center" spacing={1}>
            {task.isCompleted ? (
              <CheckIcon fontSize="small" color="success" />
            ) : (
              <UncheckedIcon fontSize="small" color="disabled" />
            )}
            <Typography
              variant="body2"
              sx={{
                textDecoration: task.isCompleted ? 'line-through' : 'none',
                color: task.isCompleted ? 'text.secondary' : 'text.primary',
              }}
            >
              {task.title}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};

export default ClientJobTaskProgress;
